import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { PaymentMethodDTO, Res, TipPaymentDTO } from 'core/api';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import * as Tips from './Tips';
import { ExternalPaymentsService } from 'pos/services';
import { Flex, Cols, Col } from 'core/ui';

import * as InvoiceStore from './InvoiceStore';
import { Message, Modal } from 'core/ui-next/modal';
import { paymentRequiresCardProcessor } from 'pos/services/ExternalPaymentsService.Common';

type Props = {
	invoice: { id: number, ticketGlobalId: Nullable<string> },
	tips: TipPaymentDTO[];
	availableMethods: PaymentMethodDTO[]
} & Modal.Actions<void>;

type State = {
	currentAmount: Nullable<number>,
	accumulatedTips: Tips.AccumulatedTip[]
};

export default abstract class UpdateTipsScreen extends PosScreenClassComponent<Props, State> {

	protected form = React.createRef<UI.Form<State>>();
	protected txtAmount = React.createRef<UI.DecimalBox<State>>();

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected getScreenId(): string {
		return 'UPDATE_TIPS';
	}

	protected getTitle(): string {
		return Res.Web.EDIT_TICKET_TIP();
	}

	protected getImage(): string | SvgIcon {
		return Images.TipSettings;
	}

	createInitialState(props: Props): Nullable<State> {
		return { accumulatedTips: Tips.getAccumulatedTips(props.tips), currentAmount: null };
	}

	onComponentDidMount(): void {
		setTimeout(() => this.txtAmount.current?.activate(), 50);
	}

	onComponentWillUnmount(): void {
	}

	protected async requestDismiss(): Promise<void> {
		this.close();
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(async () => this.close()),
			null,
			null,
			null,
			null,
			null
		];
	}

	add = async (method: PaymentMethodDTO) => {

		const valid = await this.form.current!.updateModel();
		if (!valid || !this.state.currentAmount)
			return;

		const paymentsService = ExternalPaymentsService.forPay('AddTip', this.props.invoice.ticketGlobalId);
		const payment = Tips.createPayment(method, this.state.currentAmount);
		const transactionResult = await paymentsService.register(payment);
		if (!transactionResult.ok)
			return;

		const result = await InvoiceStore.addTip(this.props.invoice.id, Tips.asNewTipPayment(payment));
		if (!result.ok) {
			await paymentsService.undoAll();
			return;
		}

		this.setState({ currentAmount: null, accumulatedTips: Tips.getAccumulatedTips(result.value) }, async () => {
			await this.form.current!.remountAll();
			setTimeout(() => this.txtAmount.current!.activate(), 100);
		});
	};

	remove = async (tip: Tips.AccumulatedTip) => {

		const method = this.props.availableMethods.find(method => method.Id === tip.MethodId);
		if (!method) {
			await Message.show(Res.Msg.MSG_THERE_IS_NOT_ANY_PAYMENT_METHOD_THAT_ALLOW_UNREGISTER_TIP());
			return;
		}

		const paymentsService = ExternalPaymentsService.forRefund('RemoveTip', this.props.invoice.ticketGlobalId);
		const payment = Tips.createPayment(method, tip.Amount);

		if (paymentRequiresCardProcessor(payment)) {
			await Message.show(Res.Msg.MSG_CANNOT_REFUND_TIP_WITH_CARD_PROCESSOR(method.Name));
			return;
		}

		const transactionResult = await paymentsService.register(payment);
		if (!transactionResult.ok)
			return;

		const result = await InvoiceStore.addTip(this.props.invoice.id, Tips.asRefundTipPayment(payment));
		if (!result.ok) {
			await paymentsService.undoAll();
			return;
		}

		this.setState({ accumulatedTips: Tips.getAccumulatedTips(result.value) }, async () => {
			await this.form.current!.remountAll();
			setTimeout(() => this.txtAmount.current!.activate(), 100);
		});
	};

	createTipList() {
		return <Flex.Vertical>
			<Flex.Item className='m-t-sm text-center text-18 font-bold'>
				{Res.Web.UPDATE_INVOICE_TIP_SET_TIPS_TOTAL_LBL(UI.Format.asMoney(this.state.accumulatedTips.sum(x => x.Amount)))}
			</Flex.Item>
			<Flex.Item>
				<Cols className='list-header'>
					<Col width='10em'>
						<span>{Res.Web.UPDATE_INVOICE_TIP_PAYMENT_METHOD_CLM()}</span>
					</Col>
					<Col width='*' className='text-right'>
						<span>{Res.Web.UPDATE_INVOICE_TIP_QUANTITY_CLM()}</span>
					</Col>
					<Col width='3em' />
				</Cols>
			</Flex.Item>
			<Flex.Item size='*'>
				<ul className='list'>
					{this.state.accumulatedTips.map(tip => {
						return <li key={tip.MethodId}>
							<Cols style={{ alignItems: 'center' }}>
								<Col width='10em'>{tip.MethodName}</Col>
								<Col width='*' className='text-right'>{UI.Format.asMoney(tip.Amount)}</Col>
								<Col width='3em' onClick={() => this.remove(tip)}><Images.Delete width={16} className='pull-right' /></Col>
							</Cols>
						</li>;
					})}
				</ul>
			</Flex.Item>
		</Flex.Vertical>;
	}
}
